import { supabase, formatSupabaseError } from '../../supabase';
import { saveCachedAccount } from '../offline/accountCache';

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function isValidEmail(value) {
  return /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(value);
}

export async function fetchOwnerProfile(authUserId) {
  if (!authUserId) {
    return { name: '', email: '', phone: '', role: 'Owner' };
  }

  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData?.user?.id) {
    throw new Error('Session expired. Sign in again.');
  }
  if (authData.user.id !== authUserId) {
    throw new Error('Unauthorized profile access.');
  }

  const { data, error } = await supabase
    .from('owners')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error && !/owners.*does not exist|schema cache/i.test(error.message)) {
    throw new Error(formatSupabaseError(error, 'Could not load profile.'));
  }

  if (data) {
    return {
      id: data.id,
      name: data.full_name || '',
      email: data.email || authData.user.email || '',
      phone: data.phone || '',
      role: 'Owner',
    };
  }

  const { data: account } = await supabase
    .from('user_accounts')
    .select('full_name, email')
    .eq('auth_user_id', authUserId)
    .eq('role', 'owner')
    .maybeSingle();

  return {
    name: account?.full_name || '',
    email: account?.email || authData.user.email || '',
    phone: '',
    role: 'Owner',
  };
}

/**
 * Authorized owner profile update.
 * Requires current password — server verifies via SECURITY DEFINER RPC.
 * No insecure table fallback (prevents bypassing password checks).
 */
export async function saveOwnerProfile(authUserId, profile, currentPassword = '') {
  if (!authUserId) throw new Error('Not logged in.');

  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData?.user?.id) {
    throw new Error('Session expired. Sign in again.');
  }
  if (authData.user.id !== authUserId) {
    throw new Error('Unauthorized: you can only update your own profile.');
  }

  const fullName = String(profile.name || '').trim();
  const email = normalizeEmail(profile.email);
  const phone = String(profile.phone || '').trim();
  const password = String(currentPassword || '');

  if (fullName.length < 2) {
    throw new Error('Full name must be at least 2 characters.');
  }
  if (!isValidEmail(email)) {
    throw new Error('Enter a valid email address.');
  }
  if (password.length < 6) {
    throw new Error('Enter your current password to authorize this update.');
  }

  const { error: rpcError } = await supabase.rpc('owner_update_own_profile', {
    p_full_name: fullName,
    p_email: email,
    p_phone: phone || null,
    p_current_password: password,
  });

  if (rpcError) {
    throw new Error(formatSupabaseError(rpcError, 'Could not save profile.'));
  }

  // Refresh session user so client Auth email matches DB
  await supabase.auth.refreshSession().catch(() => {});
  const { data: sessionData } = await supabase.auth.getSession();
  const sessionUser = sessionData?.session?.user || authData.user;

  await saveCachedAccount(
    {
      auth_user_id: authUserId,
      role: 'owner',
      full_name: fullName,
      email,
      status: 'active',
    },
    { id: sessionUser.id, email: sessionUser.email || email },
  );

  return {
    name: fullName,
    email,
    phone,
    role: 'Owner',
  };
}
